<template>
  <dx-modal :value="show" title="Montar lote" size="md" id="modal-batch-build"
            :closable="!saving" @input="close()">
    <div class="stenci-group stenci-alert">
      <p>
        Ao montar o lote, {{ lockedLabel }} (status "No lote"), o lote recebe o próximo
        número TISS do convênio e o financeiro cria o título a receber e um repasse por
        profissional. Tudo em uma única operação: se algo falhar, nada é gravado.
      </p>
    </div>

    <div class="stenci-group stenci-error" v-if="rejected.length > 0">
      <p class="text-bold">Algumas guias mudaram enquanto a tela estava aberta:</p>
      <ul>
        <li v-for="item in rejected" :key="item.accountId || item.message">{{ item.message }}</li>
      </ul>
      <button class="btn btn-sm btn-gray mt-1" @click="removeRejected">
        Remover da seleção e continuar
      </button>
    </div>

    <div class="facts build-facts">
      <div class="fact">
        <small>Convênio / plano</small>
        <div class="fact-value">{{ plan ? plan.name : '—' }}</div>
      </div>
      <div class="fact">
        <small>Tipo de guia</small>
        <div class="fact-value">{{ guideTypeName(type) }}</div>
      </div>
      <div class="fact">
        <small>Guias</small>
        <div class="fact-value">{{ guides.length }}</div>
      </div>
      <div class="fact">
        <small>Itens</small>
        <div class="fact-value">{{ items }}</div>
      </div>
      <div class="fact">
        <small>Faturado</small>
        <div class="fact-value fact-big num">{{ total | currency }}</div>
      </div>
    </div>

    <div class="cr-box">
      <div class="cr-box-title">
        <fa-icon :icon="['fal', 'hand-holding-usd']"/>Prévia do financeiro
      </div>
      <div class="fin-row fin-row-compact" v-for="(preview, index) in previews" :key="index">
        <span class="text-bold">
          Título a receber · LOTE {{ previewLabel(index) }}
          <span class="sub">
            {{ plan ? plan.name : '' }} · vencimento {{ dueLabel }}
            · {{ preview.guides }} {{ preview.guides === 1 ? 'guia' : 'guias' }}
          </span>
        </span>
        <span class="n num text-bold text-primary">{{ preview.total | currency }}</span>
        <span class="pill">a criar</span>
      </div>
      <div class="fin-row fin-row-compact" v-for="transfer in transfers" :key="transfer.name">
        <span class="text-bold">
          Repasse · {{ transfer.name }}
          <span class="sub">{{ guidesLabel(transfer.guides) }}</span>
        </span>
        <span class="n num">{{ transfer.amount | currency }}</span>
        <span class="pill">a criar</span>
      </div>
      <div class="fin-row fin-row-compact" v-if="transfers.length === 0">
        <span class="text-gray">Nenhum repasse calculado para estas guias</span>
        <span></span><span></span>
      </div>
      <small class="text-gray">
        Calculados pela regra de repasse de cada serviço. Glosas serão abatidas depois, na análise.
      </small>
    </div>

    <div class="columns mt-2">
      <div class="column col-5 col-sm-12 form-group"
           :class="{'has-error': $v.form.expectedPaymentAt.$error}">
        <label class="form-label" for="batch-expected-payment">Previsão de recebimento</label>
        <dx-input-date id="batch-expected-payment" class="form-input" placeholder="00/00/0000"
                       v-model="form.expectedPaymentAt"
                       @blur="$v.form.expectedPaymentAt.$touch()"/>
        <template v-if="$v.form.expectedPaymentAt.$error">
          <div class="form-input-hint" v-if="!$v.form.expectedPaymentAt.date">
            Data inválida
          </div>
          <div class="form-input-hint" v-else>Informe uma data a partir de hoje</div>
        </template>
        <div class="form-input-hint" v-else>{{ forecastLabel }}</div>
      </div>
      <div class="column col-7 col-sm-12 form-group" :class="{'has-error': $v.form.notes.$error}">
        <label class="form-label" for="batch-notes">Observações</label>
        <input id="batch-notes" type="text" class="form-input" maxlength="500"
               placeholder="Opcional" v-model="form.notes" @blur="$v.form.notes.$touch()">
        <div class="form-input-hint" v-if="$v.form.notes.$error">Máximo de 500 caracteres</div>
      </div>
    </div>

    <div class="build-split" v-if="overLimit">
      <div class="build-split-title">Distribuição em lotes</div>
      <span>
        {{ guides.length }} guias · limite de {{ limit }} por lote →
        <b>{{ batchCount }} lotes</b> ({{ splitLabel }})
      </span>
      <label class="form-switch">
        <input type="checkbox" v-model="form.autoSplit">
        <i class="form-icon"></i>Criar o próximo lote automaticamente
      </label>
      <div class="form-input-hint text-error" v-if="!form.autoSplit">
        Reduza para {{ limit }} guias ou ligue a criação automática
      </div>
    </div>

    <template slot="footer">
      <button class="btn btn-primary btn-icon btn-icon-left mr-1"
              :class="{loading: saving}" :disabled="saving || !form.autoSplit && overLimit"
              @click="save">
        <fa-icon :icon="['fal', 'layer-group']"/>
        {{ saving && batchCount > 1 ? 'Montando lotes…' : buildLabel }}
      </button>
      <button class="btn" :disabled="saving" @click="close()">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import moment from 'moment';
import { maxLength } from 'vuelidate/lib/validators';
import { date, minDate } from '@/data/validators';
import dateFilter from '@/filters/date';
import {
  guideTypeName, groupTransfers, sumBy, forecastHint,
} from '../service';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    plan: {
      type: Object,
      default: null,
    },
    insuranceId: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    guides: {
      type: Array,
      default: () => [],
    },
    limit: {
      type: Number,
      default: 100,
    },
    forecast: {
      type: Object,
      default: null,
    },
    rule: {
      type: Object,
      default: null,
    },
    autoSplit: {
      type: Boolean,
      default: true,
    },
    expectedPaymentAt: {
      type: String,
      default: null,
    },
  },
  data() {
    const forecastDate = this.forecast && this.forecast.date
      ? moment(this.forecast.date).format('YYYY-MM-DD')
      : '';
    return {
      saving: false,
      rejected: [],
      forecastDate,
      form: {
        expectedPaymentAt: this.expectedPaymentAt || forecastDate,
        notes: '',
        autoSplit: this.autoSplit,
      },
    };
  },
  computed: {
    manualDate() {
      const value = this.form.expectedPaymentAt || null;
      return value && value !== this.forecastDate ? value : null;
    },
    dueLabel() {
      return this.form.expectedPaymentAt ? dateFilter(this.form.expectedPaymentAt) : 'a definir';
    },
    forecastLabel() {
      if (this.manualDate) return 'Informada manualmente · a regra do plano deixa de recalcular';
      return `Regra do plano: ${forecastHint(this.rule, this.forecast)}`;
    },
    total() {
      return sumBy(this.guides, guide => guide.totals && guide.totals.informed);
    },
    items() {
      return this.guides.reduce((sum, guide) => sum + (guide.itemsCount || 0), 0);
    },
    transfers() {
      return groupTransfers(this.guides);
    },
    overLimit() {
      return this.guides.length > this.limit;
    },
    batchCount() {
      return Math.max(1, Math.ceil(this.guides.length / this.limit));
    },
    chunks() {
      const chunks = [];
      for (let index = 0; index < this.guides.length; index += this.limit) {
        chunks.push(this.guides.slice(index, index + this.limit));
      }
      return chunks;
    },
    previews() {
      return this.chunks.map(chunk => ({
        guides: chunk.length,
        total: sumBy(chunk, guide => guide.totals && guide.totals.informed),
      }));
    },
    splitLabel() {
      return `${this.chunks.map(chunk => chunk.length).join(' + ')} guias`;
    },
    buildLabel() {
      return this.batchCount > 1 ? `Montar ${this.batchCount} lotes` : 'Montar lote';
    },
    lockedLabel() {
      return this.guides.length === 1
        ? 'a guia fica travada'
        : `as ${this.guides.length} guias ficam travadas`;
    },
  },
  validations() {
    return {
      form: {
        expectedPaymentAt: {
          date,
          minDate: minDate(moment().format('YYYY-MM-DD')),
        },
        notes: { maxLength: maxLength(500) },
      },
    };
  },
  methods: {
    guideTypeName,
    dateFilter,
    previewLabel(index) {
      return this.previews.length > 1 ? `${index + 1} de ${this.previews.length}` : '';
    },
    guidesLabel(count) {
      return count === 1 ? '1 guia' : `${count} guias`;
    },
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;
      if (this.overLimit && !this.form.autoSplit) return;

      this.saving = true;
      this.rejected = [];

      const payload = {
        planId: this.plan.id,
        insuranceId: this.insuranceId || undefined,
        guideType: this.type,
        accountIds: this.guides.map(({ id }) => id),
        notes: this.form.notes || null,
        expectedPaymentAt: this.manualDate,
        splitIntoBatches: this.overLimit && this.form.autoSplit,
      };

      this.$http.post('/billing-batches', payload)
        .then(({ data }) => {
          const batches = data.batches || [data.batch];
          const numbers = batches.map(({ number }) => number);
          this.$toast.success(batches.length === 1
            ? `Lote ${numbers[0]} montado com ${this.guides.length} ${this.guides.length === 1 ? 'guia' : 'guias'}.`
            : `${batches.length} lotes montados (${numbers.join(', ')}).`);
          (data.warnings || [])
            .filter(({ level }) => level === 'warn')
            .forEach(({ message }) => this.$toast.warning(message));
          this.$emit('built', { batches, removedIds: payload.accountIds });
        })
        .catch((e) => {
          const response = e && e.response && e.response.data;
          if (response && response.accountId) {
            this.rejected = [{ accountId: response.accountId, message: response.message }];
            return;
          }
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    removeRejected() {
      const ids = this.rejected.map(({ accountId }) => accountId);
      this.rejected = [];
      this.$emit('remove-guides', ids);
    },
    close() {
      if (this.saving) return;
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
@import "../batch.scss";

#modal-batch-build {
  .build-facts {
    grid-template-columns: repeat(5, minmax(0, 1fr));
    margin-top: $layout-spacing;
  }
  .stenci-error ul {
    margin: $layout-spacing-sm 0 0 $layout-spacing-lg;
  }
  .build-split {
    background-color: $light-color;
    border: $border-color-dark solid $border-width;
    border-radius: $border-radius;
    font-size: $font-size-sm;
    margin-top: $layout-spacing;
    padding: $layout-spacing $layout-spacing-lg;
    .build-split-title {
      font-weight: 700;
      margin-bottom: .2rem;
    }
    .form-switch {
      margin-top: .3rem;
    }
  }
  @media screen and (max-width: $size-sm) {
    .build-facts {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}
</style>
